import { Suspense, lazy, useEffect } from 'react';
import shallow from 'zustand/shallow';
import { useLocation, useNavigate, Routes, Route } from 'react-router-dom';

import { useAuth } from './lib/auth-provider/context';
import { useProfile } from './lib/auth-provider/context/hooks';

import { FullPageSpinner } from './common/components';
import ListMading from './modules/school/mading/';
import ListAbsensi from './modules/school/attendance';
import ListArticle from './modules/school/artikel/';
import ProtectedRoute from './components/ProtectedRoute';

const FullPageError = lazy(() => import('./common/components/full-page-error'));
const Home = lazy(() => import('./modules/home'));
const LoginModules = lazy(() => import('./modules/login'));
// const ExampleModules = lazy(() => import("./modules/example"));
const SchoolListModules = lazy(() => import('./modules/school/register'));
const SchoolBillingModules = lazy(() => import('./modules/school/billing'));
const SchoolAdminModules = lazy(() => import('./modules/school/admin'));
const SchoolStudentModules = lazy(() => import('./modules/school/student'));
const SchoolTeacherModules = lazy(() => import('./modules/school/teacher'));
const CourseModules = lazy(() => import('./modules/school/course'));
const DashboardModules = lazy(() => import('./modules/dashboard'));
const QrCodeManagement = lazy(
  () => import('./modules/kantinPay/QrCodeManagement'),
);
const CardsManagement = lazy(
  () => import('./modules/kantinPay/CardsManagement'),
);
const CardActivation = lazy(() => import('./modules/kantinPay/CardActivation'));
const KantinPayBusinessDashboard = lazy(
  () => import('./modules/kantinPay/BusinessDashboard'),
);
const MerchantsManagement = lazy(() => import('./modules/kantinPay/Merchants'));
const RFIDCardsManagement = lazy(() => import('./modules/rfidCards'));
const DeviceManagement = lazy(() => import('./modules/deviceManagement'));
const KioskManagement = lazy(() => import('./modules/kioskManagement'));
const RfidDevices = lazy(() => import('./modules/rfidDevices'));
const TailscaleMonitoring = lazy(() => import('./modules/tailscaleMonitoring'));
const YayasanModules = lazy(() => import('./modules/yayasan'));
const YayasanDashboard = lazy(() => import('./modules/yayasan-dashboard'));
const DigitalLibraryModules = lazy(() => import('./modules/digitalLibrary'));
const FreemiumAnalyticsModules = lazy(
  () => import('./modules/freemium-analytics'),
);
const SchoolRegistrationModules = lazy(
  () => import('./modules/school-registration'),
);
const GamificationModules = lazy(() => import('./modules/gamification'));
const TeacherGamificationModules = lazy(
  () => import('./modules/TeacherGamification'),
);
const ParentInvitationsModules = lazy(
  () => import('./modules/parent-invitations'),
);
const ParentsModules = lazy(() => import('./modules/parents'));
const VerifikasiNomorModules = lazy(
  () => import('./modules/parent-verification'),
);
const PasPhotoDownloadModules = lazy(
  () => import('./modules/school/pas-photo-download'),
);
const PencairanModules = lazy(() => import('./modules/pencairan'));
const PreaccountModules = lazy(() => import('./modules/preaccounts'));
const PricingModules = lazy(() => import('./modules/pricing'));
const UnggulpayFeeModules = lazy(() => import('./modules/unggulpay-fee'));
const UnggulpayEnterpriseModules = lazy(
  () => import('./modules/unggulpay-enterprise'),
);

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [getAuth] = useAuth((state) => [state.getAuth], shallow);

  const { refetch: getProfile } = useProfile();

  useEffect(() => {
    if (location.pathname === '/') {
      navigate('/home');
    }
  }, [location, navigate]);

  useEffect(() => {
    const currentUser = getAuth();
    if (currentUser.token) {
      getProfile();
    }
  }, [getAuth, getProfile]);

  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Routes>
        <Route path="/home/*" element={<Home />} />
        <Route path="/login/*" element={<LoginModules />} />

        {/* Dashboard Routes */}
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute requiredRole={'superadmin'}>
              <DashboardModules />
            </ProtectedRoute>
          }
        />

        {/* <Route path="/example/*" element={<ExampleModules />} /> */}
        <Route path="/school/register/*" element={<SchoolListModules />} />
        <Route path="/school/course/*" element={<CourseModules />} />
        <Route path="/school/billing/*" element={<SchoolBillingModules />} />
        <Route path="/school/attendance/*" element={<ListAbsensi />} />
        <Route path="/school/admin/*" element={<SchoolAdminModules />} />
        <Route path="/school/student/*" element={<SchoolStudentModules />} />
        <Route path="/school/teacher/*" element={<SchoolTeacherModules />} />
        <Route
          path="/school/pas-photo-download/*"
          element={<PasPhotoDownloadModules />}
        />
        <Route path="/school/bulletin-board/*" element={<ListMading />} />
        <Route path="/school/article/*" element={<ListArticle />} />

        {/* KantinPay Routes */}
        <Route
          path="/kantinpay/dashboard"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <KantinPayBusinessDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kantinpay/qr-management"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <QrCodeManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kantinpay/cards"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <CardsManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kantinpay/card-activation"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <CardActivation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kantinpay/merchants"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <MerchantsManagement />
            </ProtectedRoute>
          }
        />

        {/* Pencairan saldo sekolah (Payment-Backend).
            requiredRole HARUS 'superadmin' — itu string yang benar-benar
            dipulangkan SuperAdminMiddleware.js:49. Rute yang memakai
            "super_admin" akan memantulkan superadmin ke /home tanpa galat.
            Tanpa pembungkus ini, non-superadmin yang membuka URL-nya sampai
            ke layar, kena 401 dari API, dan interceptor di
            lib/client/handler.js:14 menghapus storage lalu melempar ke
            /login — sesi mereka hancur, bukan sekadar ditolak. */}
        <Route
          path="/pencairan/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <PencairanModules />
            </ProtectedRoute>
          }
        />

        {/* Langganan SIS (S2a): Katalog Harga + antrean "Perlu ditinjau".
            Endpoint-nya di balik verifyTokenAdmin (hanya superadmin); alasan
            pembungkusnya sama dengan /pencairan di atas. */}
        <Route
          path="/pricing/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <PricingModules />
            </ProtectedRoute>
          }
        />

        <Route path="/preaccounts/*" element={<ProtectedRoute requiredRole="superadmin"><PreaccountModules /></ProtectedRoute>} />

        {/* Biaya layanan UnggulPay (S2c): setelan di balik verifyTokenAdmin
            (backend-core), pendapatan di balik verifySuperAdmin
            (Payment-Backend) — keduanya hanya superadmin. */}
        <Route
          path="/unggulpay-fee/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <UnggulpayFeeModules />
            </ProtectedRoute>
          }
        />

        {/* UnggulPay Enterprise (spec UnggulPay §5): endpoint di balik verifyTokenAdmin
            backend-core — hanya superadmin; alasan pembungkusnya sama dengan /pencairan. */}
        <Route
          path="/unggulpay-enterprise/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <UnggulpayEnterpriseModules />
            </ProtectedRoute>
          }
        />

        {/* RFID Cards Management.

            requiredRole 'superadmin' bukan pengetatan sepihak. ENAM rute
            mendapat pembungkus yang sama dan alasan yang sama — dan keenamnya
            TIDAK berurutan di berkas ini, jadi disebut namanya:
            /rfid-cards, /kiosks, /rfid-devices, /parent-invitations,
            /parents, /verifikasi-nomor.

            Keenamnya memanggil endpoint di balik verifyTokenAdmin, yang
            menolak apa pun selain SUPERUSER_ROLE = 'superadmin' (backend-core
            BackOfficeMiddleware.js:8, ditegakkan :57). Bukan verifyInternalUser
            — yang itu juga menerima kepala_yayasan, dan dipakai tetangga
            seperti /yayasan.

            CATATAN yang mudah salah: MENU_ITEMS BUKAN "menu superadmin".
            navigation-drawer/index.jsx:26-31 menyajikannya ke SETIAP peran
            kecuali kepala_yayasan, dan AdminModel.js:41 default-nya 'admin'.
            Jadi pengguna admin/pic MELIHAT keenam tautan ini dan sejak
            perubahan ini dipantulkan diam-diam ke /home. Yang membuat
            perubahan ini aman adalah penjaga backend-nya, bukan daftar menu.

            Tanpa pembungkus ini, non-superadmin yang membuka URL-nya sampai
            ke layar, kena 401 dari API, dan interceptor di
            lib/client/handler.js:14 menghapus storage lalu melempar ke
            /login — sesi mereka hancur, bukan sekadar ditolak. Keenam modul
            memakai lib/client yang BERSAMA itu. */}
        <Route
          path="/rfid-cards/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <RFIDCardsManagement />
            </ProtectedRoute>
          }
        />

        {/* Device Management */}
        <Route
          path="/devices/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <DeviceManagement />
            </ProtectedRoute>
          }
        />

        {/* Kiosk Management */}
        <Route
          path="/kiosks/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <KioskManagement />
            </ProtectedRoute>
          }
        />

        {/* RFID Readers (ESP32) */}
        <Route
          path="/rfid-devices/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <RfidDevices />
            </ProtectedRoute>
          }
        />

        {/* Tailscale Monitoring */}
        <Route
          path="/tailscale/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <TailscaleMonitoring />
            </ProtectedRoute>
          }
        />

        {/* Yayasan Management - Superadmin only */}
        <Route
          path="/yayasan/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <YayasanModules />
            </ProtectedRoute>
          }
        />

        {/* Parent Invitations - Superadmin only */}
        <Route
          path="/parent-invitations/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <ParentInvitationsModules />
            </ProtectedRoute>
          }
        />

        {/* Parent Directory - Superadmin.
            Komentar lama menyebut "backend verifyTokenAdmin enforces role"
            sebagai alasan tidak memasang penjaga. Itu benar untuk DATA dan
            tidak cukup untuk SESI: 401-nya justru yang menghancurkannya. */}
        <Route
          path="/parents/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <ParentsModules />
            </ProtectedRoute>
          }
        />

        {/* Verifikasi Nomor - Superadmin only */}
        <Route
          path="/verifikasi-nomor/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <VerifikasiNomorModules />
            </ProtectedRoute>
          }
        />

        {/* Digital Library Management - Superadmin only */}
        <Route
          path="/digital-library/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <DigitalLibraryModules />
            </ProtectedRoute>
          }
        />

        {/* Freemium Analytics - Superadmin only */}
        <Route
          path="/freemium-analytics/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <FreemiumAnalyticsModules />
            </ProtectedRoute>
          }
        />

        {/* Yayasan Dashboard - Kepala Yayasan only */}
        <Route
          path="/yayasan-dashboard/*"
          element={
            <ProtectedRoute requiredRole="kepala_yayasan">
              <YayasanDashboard />
            </ProtectedRoute>
          }
        />

        {/* School Registration Approval - Superadmin only */}
        <Route
          path="/school-registration/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <SchoolRegistrationModules />
            </ProtectedRoute>
          }
        />

        {/* Gamification Dashboard - Superadmin only */}
        <Route
          path="/gamification/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <GamificationModules />
            </ProtectedRoute>
          }
        />

        {/* Teacher Gamification Dashboard - Superadmin only */}
        <Route
          path="/teacher-gamification/*"
          element={
            <ProtectedRoute requiredRole="superadmin">
              <TeacherGamificationModules />
            </ProtectedRoute>
          }
        />

        <Route
          path="*"
          element={<FullPageError message="Page not found" errorCode={404} />}
        />
      </Routes>
    </Suspense>
  );
}

export default App;
